import type { Bindings } from '../input/actions';
import type { GameSettings, GraphicsQuality } from '../save/saveData';
import { formatMoney } from '../economy/wallet';
import { LOADING_ART_URL, pastePhoto, TITLE_ART_URL, TITLE_TOUCH_ART_URL } from './art';
import { el, esc, html, scrollCue, setText, toggle } from './dom';
import { keyLabel, stepFocus } from './format';
import type { MissionIntro, ResultInfo } from './uiTypes';

export type { NavAction } from './padNav';
import type { NavAction } from './padNav';
export type Snd = 'click' | 'confirm' | 'back' | 'tick' | 'stamp' | 'cash' | 'denied' | 'whoosh' | 'type';

/** What every menu-like screen offers the input router. */
export interface Screen {
  readonly root: HTMLElement;
  /** Return true when the action was consumed. */
  onNav(a: NavAction): boolean;
  dispose(): void;
}

export interface ScreenHost {
  sound(s: Snd): void;
  settings(): GameSettings;
  setSettings(patch: Partial<GameSettings>): void;
  bindings(): Bindings;
  /** The render module's actual tier right now ('low' | 'medium' | 'high'), if known (shown next to AUTO). */
  qualityTier?(): string | null;
  /** The touch UI is on (phone / tablet): screens add touch settings and tap-friendly wording. */
  touch?(): boolean;
  /** Settings → Time of day: what it shows ("AUTO · 21:40", "NIGHT") and stepping through the choices. */
  timeOfDay?(): string;
  cycleTimeOfDay?(dir: 1 | -1): void;
  /** Onboarding tips on/off (src/onboarding); the settings screen shows a row when present. */
  tips?: { get(): boolean; set(on: boolean): void };
}

// ----------------------------------------------------------------------------- vertical menu list

interface MenuEntry {
  id: string;
  label: string;
  hint?: string;
  enabled?: () => boolean;
}

/** Skewed poster-strip menu; hover moves focus, click activates. */
class MenuList {
  readonly ul: HTMLUListElement;
  index = 0;
  private lis: HTMLLIElement[] = [];

  constructor(
    parent: HTMLElement,
    private entries: MenuEntry[],
    private readonly host: ScreenHost,
    private readonly onActivate: (id: string) => void,
  ) {
    this.ul = el('ul', 'bh-menu', undefined, parent);
    this.render();
    this.index = this.entries.findIndex((e) => e.enabled?.() ?? true);
    if (this.index < 0) this.index = 0;
    this.paintFocus();
  }

  render(): void {
    this.ul.textContent = '';
    this.lis = this.entries.map((e, i) => {
      const li = el('li', '', undefined, this.ul);
      const item = el('div', 'item', e.label, li);
      if (e.hint) el('small', '', e.hint, item);
      li.addEventListener('pointerenter', () => {
        if (this.isEnabled(i) && this.index !== i) {
          this.index = i;
          this.paintFocus();
          this.host.sound('tick');
        }
      });
      li.addEventListener('click', () => {
        if (!this.isEnabled(i)) return this.host.sound('denied');
        this.index = i;
        this.paintFocus();
        this.activate();
      });
      return li;
    });
    this.paintFocus();
  }

  private isEnabled(i: number): boolean {
    return this.entries[i]?.enabled?.() ?? true;
  }

  focus(id: string): void {
    const i = this.entries.findIndex((e) => e.id === id);
    if (i >= 0) {
      this.index = i;
      this.paintFocus();
    }
  }

  paintFocus(): void {
    this.lis.forEach((li, i) => {
      toggle(li, 'focus', i === this.index);
      toggle(li, 'disabled', !this.isEnabled(i));
    });
  }

  move(dir: 1 | -1): void {
    const next = stepFocus(this.index, dir, this.entries.map((_, i) => this.isEnabled(i)));
    if (next !== this.index) {
      this.index = next;
      this.paintFocus();
      this.host.sound('tick');
    }
  }

  activate(): void {
    const e = this.entries[this.index];
    if (!e) return;
    if (!this.isEnabled(this.index)) return this.host.sound('denied');
    this.host.sound('confirm');
    this.onActivate(e.id);
  }

  nav(a: NavAction): boolean {
    if (a === 'up') this.move(-1);
    else if (a === 'down') this.move(1);
    else if (a === 'confirm') this.activate();
    else return false;
    return true;
  }
}

// ----------------------------------------------------------------------------- title

export interface TitleCallbacks {
  hasProgress: boolean;
  onContinue(): void;
  onNewGame(): void;
  onSettings(): void;
  /** The plain controls sheet (reachable through `uiScreen('controls')`; How to play draws the controls, so the menu lists only that). */
  onControls?(): void;
  /** How to play (illustrated help page). */
  onHelp?(): void;
  /** Present when the online module is loaded: opens sign-in / city selection. */
  onOnline?(): void;
}

export class TitleScreen implements Screen {
  readonly root: HTMLElement;
  private menu: MenuList;
  private confirm: HTMLElement | null = null;
  private confirmMenu: MenuList | null = null;
  private readonly block: HTMLElement;

  constructor(parent: HTMLElement, private readonly host: ScreenHost, private readonly cb: TitleCallbacks) {
    this.root = el('div', 'bh-screen bh-title', undefined, parent);
    // The painted street behind the menu (docs/UI.md, Painted art): fetched only now, faded in once decoded,
    // the flyover shows until then (and for good if the file never comes).
    // The loading screen's smaller copy of the same painting is already in the cache, so the street is there
    // from the first frame and the sharp one simply lands on top.
    const art = el('div', 'art', undefined, this.root);
    art.setAttribute('aria-hidden', 'true');
    const under = new Image();
    under.onload = () => {
      if (!art.classList.contains('ready')) {
        art.style.backgroundImage = `url("${LOADING_ART_URL}")`;
        art.classList.add('ready');
      }
    };
    under.src = LOADING_ART_URL;
    // A phone puts the logo left and the menu right, so it gets the painting with the man in the middle.
    const sharp = document.documentElement.classList.contains('bh-touch') ? TITLE_TOUCH_ART_URL : TITLE_ART_URL;
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      art.style.backgroundImage = `url("${sharp}"), url("${LOADING_ART_URL}")`;
      art.classList.add('ready');
    };
    img.src = sharp;
    const mast = el('div', 'masthead', undefined, this.root);
    mast.innerHTML = '<span><b>THE BOROUGH GAZETTE</b> · LATE FINAL EDITION</span><span>LOW BOROUGH · OCTOBER 1970 · 3d</span>';

    this.block = el('div', 'block', undefined, this.root);
    const logo = el('div', 'bh-logo', undefined, this.block);
    el('span', 'l1', 'BOROUGH', logo);
    el('span', 'l2', 'HEAT', logo);
    html('span', 'tag bh-stamp', '1970', logo);
    el('div', 'bh-sub', 'One borough. Four hundred coppers. A van full of other people’s money.', this.block);

    this.menu = new MenuList(
      this.block,
      [
        ...(cb.onOnline ? [{ id: 'online', label: 'Play online', hint: 'the shared city' }] : []),
        { id: 'continue', label: 'Continue', hint: cb.hasProgress ? (cb.onOnline ? 'offline · saved job' : 'saved job') : 'no save yet', enabled: () => cb.hasProgress },
        { id: 'new', label: 'New game', ...(cb.onOnline ? { hint: 'offline' } : {}) },
        { id: 'settings', label: 'Settings' },
        // One answer for "how do I…": How to play draws every control; the plain Controls sheet is not listed twice.
        ...(cb.onHelp ? [{ id: 'help', label: 'How to play', hint: 'the loop, the controls, the law' }] : [{ id: 'controls', label: 'Controls' }]),
      ],
      host,
      (id) => this.activate(id),
    );
    this.menu.focus(cb.hasProgress ? 'continue' : 'new');

    const foot = el('div', 'foot', undefined, this.root);
    foot.innerHTML = '<span>↑ ↓ choose · Enter confirm · Esc back</span><span>A fictional city. Any resemblance to persons living, dead or dodgy is coincidental.</span>';
  }

  private activate(id: string): void {
    if (id === 'continue') this.cb.onContinue();
    else if (id === 'online') this.cb.onOnline?.();
    else if (id === 'settings') this.cb.onSettings();
    else if (id === 'controls') this.cb.onControls?.();
    else if (id === 'help') this.cb.onHelp?.();
    else if (id === 'new') {
      if (this.cb.hasProgress) this.askOverwrite();
      else this.cb.onNewGame();
    }
  }

  private askOverwrite(): void {
    this.block.hidden = true;
    this.confirm = el('div', 'confirm bh-paper', undefined, this.root);
    el('h3', '', 'Start over?', this.confirm);
    el('p', '', 'Your cash, finished jobs and unlocks will be torn up. Settings stay.', this.confirm);
    this.confirmMenu = new MenuList(
      this.confirm,
      [
        { id: 'no', label: 'Keep my save' },
        { id: 'yes', label: 'Wipe it' },
      ],
      this.host,
      (id) => {
        if (id === 'yes') this.cb.onNewGame();
        else this.closeConfirm();
      },
    );
  }

  private closeConfirm(): void {
    this.confirm?.remove();
    this.confirm = null;
    this.confirmMenu = null;
    this.block.hidden = false;
  }

  onNav(a: NavAction): boolean {
    if (this.confirmMenu) {
      if (a === 'back') {
        this.host.sound('back');
        this.closeConfirm();
        return true;
      }
      return this.confirmMenu.nav(a);
    }
    return this.menu.nav(a);
  }

  dispose(): void {
    this.root.remove();
  }
}

// ----------------------------------------------------------------------------- pause

export interface PauseCallbacks {
  onResume(): void;
  onSettings(): void;
  onControls?(): void;
  onHelp?(): void;
  /** "Report a problem" (src/feedback): shown when the feedback module is there. */
  onReport?(): void;
  /** "Your profile" (src/profile): you and your motor, the record on a card. */
  onProfile?(): void;
  onQuit(): void;
  stats(): { label: string; value: string }[];
}

export class PauseScreen implements Screen {
  readonly root: HTMLElement;
  private readonly menu: MenuList;

  constructor(parent: HTMLElement, host: ScreenHost, cb: PauseCallbacks) {
    this.root = el('div', 'bh-screen bh-pause', undefined, parent);
    const stack = el('div', 'stack', undefined, this.root);
    el('h1', '', 'Lying low', stack);
    this.menu = new MenuList(
      stack,
      [
        { id: 'resume', label: 'Back to it' },
        ...(cb.onProfile ? [{ id: 'profile', label: 'Your profile', hint: 'you, your motor, the record' }] : []),
        { id: 'settings', label: 'Settings' },
        ...(cb.onHelp ? [{ id: 'help', label: 'How to play' }] : [{ id: 'controls', label: 'Controls' }]),
        ...(cb.onReport ? [{ id: 'report', label: 'Report a problem' }] : []),
        { id: 'quit', label: 'Quit to title' },
      ],
      host,
      (id) => {
        if (id === 'resume') cb.onResume();
        else if (id === 'profile') cb.onProfile?.();
        else if (id === 'settings') cb.onSettings();
        else if (id === 'controls') cb.onControls?.();
        else if (id === 'help') cb.onHelp?.();
        else if (id === 'report') cb.onReport?.();
        else if (id === 'quit') cb.onQuit();
      },
    );
    const sheet = el('div', 'sheet bh-paper', undefined, this.root);
    el('h3', '', 'The story so far', sheet);
    const dl = el('dl', '', undefined, sheet);
    for (const s of cb.stats()) {
      el('dt', '', s.label, dl);
      el('dd', '', s.value, dl);
    }
  }

  onNav(a: NavAction): boolean {
    return this.menu.nav(a);
  }

  dispose(): void {
    this.root.remove();
  }
}

// ----------------------------------------------------------------------------- settings

interface Row {
  id: string;
  label: string;
  /** One line under the label saying what the row does (and which way the slider goes). */
  hint?: string;
  /** A ruled heading above this row; rows that share one sit together (SOUND, PICTURE, DRIVING…). */
  section?: string;
  kind: 'slider' | 'choice' | 'button';
  get?: () => number | string;
  set?: (v: number | string) => void;
  /** Choice rows: step through the options. */
  cycle?: (dir: 1 | -1) => void;
  step?: number;
}

const QUALITY: GraphicsQuality[] = ['auto', 'low', 'medium', 'high'];
/** The settings rows in their sections. Every one persists as it changes (the save for most, the borough clock and the tips in their own localStorage keys). */
export function settingsRows(host: ScreenHost): Row[] {
  const s = (): GameSettings => host.settings();
  const touch = host.touch?.() ?? false;
  return [
    { section: 'Sound', id: 'master', label: 'Master volume', hint: 'Everything at once.', kind: 'slider', get: () => s().master, set: (v) => host.setSettings({ master: +v }) },
    { section: 'Sound', id: 'music', label: 'Radio / music', hint: 'The car radio and the title music.', kind: 'slider', get: () => s().music, set: (v) => host.setSettings({ music: +v }) },
    { section: 'Sound', id: 'sfx', label: 'Effects', hint: 'Engines, sirens, crashes, the bleeper.', kind: 'slider', get: () => s().sfx, set: (v) => host.setSettings({ sfx: +v }) },
    {
      section: 'Picture',
      id: 'graphics',
      label: 'Graphics quality',
      hint: touch ? 'AUTO follows the frame rate (a phone never goes above MEDIUM). LOW drops shadows.' : 'AUTO follows the frame rate. LOW drops shadows, post-processing and the crowd.',
      kind: 'choice',
      get: () => {
        const g = s().graphics;
        const tier = g === 'auto' ? host.qualityTier?.() : null;
        return tier ? `AUTO (${tier.toUpperCase()})` : g.toUpperCase();
      },
      cycle: (dir) => {
        const idx = (QUALITY.indexOf(s().graphics) + dir + QUALITY.length) % QUALITY.length;
        host.setSettings({ graphics: QUALITY[idx]! });
      },
    },
    ...(host.timeOfDay
      ? [{ section: 'Picture', id: 'timeOfDay', label: 'Time of day', hint: 'AUTO runs the borough clock, a day in about half an hour; DAY, DUSK and NIGHT hold still. Online the city sets the clock and the weather for everyone.', kind: 'choice' as const, get: () => host.timeOfDay!(), cycle: (dir: 1 | -1) => host.cycleTimeOfDay?.(dir) }]
      : []),
    // Right = closer: the slider runs from the far 1.2× follow distance to the close 0.55×.
    { section: 'Picture', id: 'zoom', label: 'Camera zoom', hint: 'How close the camera follows. Right is closer.', kind: 'slider', get: () => (1.2 - s().cameraZoom) / 0.65, set: (v) => host.setSettings({ cameraZoom: +(1.2 - +v * 0.65).toFixed(3) }) },
    { section: 'Picture', id: 'shake', label: 'Camera shake', hint: 'How much crashes and gunfire rock the picture. Left is none.', kind: 'slider', get: () => s().cameraShake, set: (v) => host.setSettings({ cameraShake: +v }) },
    // Driving feel (docs/DRIVING.md §8): one knob for how quick the wheel answers, one switch for the assists.
    { section: 'Driving', id: 'steerSensitivity', label: 'Steering sensitivity', hint: 'How quickly the wheel answers keys, pad or stick. Right is sharper.', kind: 'slider', get: () => s().steerSensitivity - 0.5, set: (v) => host.setSettings({ steerSensitivity: +(0.5 + +v).toFixed(2) }) },
    {
      section: 'Driving',
      id: 'driveAssists',
      label: 'Driving assists',
      hint: touch ? 'Keeps the car along the road and backs it off a wall it is stuck on. OFF is the raw car.' : 'Gentler steering at speed and a steadier corner. OFF is the raw car.',
      kind: 'choice',
      get: () => (s().driveAssists ? 'ON' : 'OFF'),
      cycle: () => host.setSettings({ driveAssists: !s().driveAssists }),
    },
    ...(touch ? touchRows(host) : []),
    {
      section: 'Help',
      id: 'routeArrows',
      label: 'Route arrows',
      hint: 'Arrows on the road show the way to your job or the place you marked.',
      kind: 'choice',
      get: () => (s().routeArrows ? 'ON' : 'OFF'),
      cycle: () => host.setSettings({ routeArrows: !s().routeArrows }),
    },
    ...(host.tips
      ? [{ section: 'Help', id: 'tips', label: 'Tips and pointers', hint: 'The little slips that explain things as they come up; each goes once followed.', kind: 'choice' as const, get: () => (host.tips!.get() ? 'ON' : 'OFF'), cycle: () => host.tips!.set(!host.tips!.get()) }]
      : []),
    { id: 'back', label: 'Done', kind: 'button' },
  ];
}

export class SettingsScreen implements Screen {
  readonly root: HTMLElement;
  private index = 0;
  private readonly rows: Row[];
  private readonly lis: HTMLLIElement[] = [];
  private readonly vals: HTMLElement[] = [];
  private readonly inputs = new Map<number, HTMLInputElement>();

  constructor(parent: HTMLElement, private readonly host: ScreenHost, private readonly onBack: () => void) {
    this.root = el('div', 'bh-screen bh-settings', undefined, parent);
    const panel = el('div', 'panel bh-paper', undefined, this.root);
    // The masthead stays put while the rows scroll: the title and a ✕ (on a phone the only way out used
    // to be the DONE row two screens down).
    const head = el('div', 'head', undefined, panel);
    el('h2', '', 'Settings', head);
    const close = el('button', 'close bh-x', '✕', head);
    close.type = 'button';
    close.setAttribute('aria-label', 'Done');
    close.title = 'Done (Esc)';
    close.addEventListener('click', (e) => {
      e.stopPropagation();
      host.sound('back');
      this.onBack();
    });
    const ul = el('ul', 'bh-rows', undefined, panel);
    const cue = scrollCue(ul); // a torn-edge fade at the foot while rows sit below the fold (a phone shows four of fourteen)
    this.rows = settingsRows(host);
    let section: string | undefined;
    this.rows.forEach((r, i) => {
      if (r.section && r.section !== section) {
        section = r.section;
        const h = el('li', 'sec', undefined, ul);
        h.setAttribute('aria-hidden', 'true');
        el('span', '', r.section, h);
      }
      const li = el('li', r.kind === 'button' ? 'btn' : '', undefined, ul);
      const lbl = el('span', 'lbl', undefined, li);
      el('b', '', r.label, lbl);
      if (r.hint) el('small', 'hint', r.hint, lbl);
      const val = el('span', 'val', undefined, li);
      if (r.kind === 'slider') {
        const input = el('input', '', undefined, val);
        input.type = 'range';
        input.min = '0';
        input.max = '100';
        input.value = String(Math.round(+(r.get!()) * 100));
        const pct = el('span', 'pct', '', val);
        input.addEventListener('input', () => {
          r.set!(+input.value / 100);
          this.refresh();
        });
        input.addEventListener('change', () => host.sound('tick'));
        this.inputs.set(i, input);
        this.vals.push(pct);
      } else {
        this.vals.push(val);
      }
      li.addEventListener('pointerenter', () => {
        if (this.index !== i) {
          this.index = i;
          this.paint();
          host.sound('tick');
        }
      });
      li.addEventListener('click', (e) => {
        if (r.kind === 'slider' && (e.target as HTMLElement).tagName === 'INPUT') return;
        this.index = i;
        this.paint();
        this.onNav('confirm');
      });
      this.lis.push(li);
    });
    if (host.touch?.()) {
      ul.dataset.touchScroll = '';
      html('div', 'bh-foot', '<span>Tap a row to change it · drag the sliders</span><span>Done closes · everything saves as you go</span>', panel);
    } else html('div', 'bh-foot', '<span>↑ ↓ choose</span><span>← → adjust</span><span>Esc back</span><span>Saved as you go</span>', panel);
    this.refresh();
    this.paint();
    cue();
  }

  private refresh(): void {
    this.rows.forEach((r, i) => {
      if (r.kind === 'slider') {
        const v = Math.round(+(r.get!()) * 100);
        this.vals[i]!.textContent = String(v);
        const input = this.inputs.get(i)!;
        if (+input.value !== v) input.value = String(v);
      } else if (r.kind === 'choice') {
        this.vals[i]!.textContent = `◂ ${r.get!()} ▸`;
      } else this.vals[i]!.textContent = '';
    });
  }

  private paint(): void {
    this.lis.forEach((li, i) => toggle(li, 'focus', i === this.index));
    // Keyboard / pad: keep the chosen row in view when the list scrolls (phones, small windows).
    this.lis[this.index]?.scrollIntoView?.({ block: 'nearest' });
  }

  onNav(a: NavAction): boolean {
    const r = this.rows[this.index]!;
    if (a === 'up' || a === 'down') {
      this.index = stepFocus(this.index, a === 'up' ? -1 : 1, this.rows.map(() => true));
      this.paint();
      this.host.sound('tick');
      return true;
    }
    if (a === 'back') {
      this.host.sound('back');
      this.onBack();
      return true;
    }
    if (a === 'confirm') {
      if (r.kind === 'button') {
        this.host.sound('confirm');
        this.onBack();
      } else if (r.kind === 'choice') this.adjust(r, 1);
      return true;
    }
    if (a === 'left' || a === 'right') {
      this.adjust(r, a === 'left' ? -1 : 1);
      return true;
    }
    return false;
  }

  private adjust(r: Row, dir: 1 | -1): void {
    if (r.kind === 'slider') {
      const v = Math.max(0, Math.min(1, +(r.get!()) + dir * 0.1));
      r.set!(Math.round(v * 10) / 10);
    } else if (r.kind === 'choice') {
      r.cycle?.(dir);
    } else return;
    this.host.sound('tick');
    this.refresh();
  }

  dispose(): void {
    this.root.remove();
  }
}

/** Settings rows for the on-screen controls (only on touch devices). Sliders map 0..1 onto each range. */
function touchRows(host: ScreenHost): Row[] {
  const s = (): GameSettings => host.settings();
  const onOff = (v: boolean): string => (v ? 'ON' : 'OFF');
  const section = 'Touch controls';
  return [
    { section, id: 'touchSize', label: 'Touch controls size', hint: 'The knobs, pedals and stick. Nothing goes under 44 px.', kind: 'slider', get: () => (s().touchSize - 0.7) / 0.7, set: (v) => host.setSettings({ touchSize: +(0.7 + +v * 0.7).toFixed(3) }) },
    { section, id: 'touchOpacity', label: 'Touch controls opacity', hint: 'How solid the controls draw over the street.', kind: 'slider', get: () => (s().touchOpacity - 0.2) / 0.8, set: (v) => host.setSettings({ touchOpacity: +(0.2 + +v * 0.8).toFixed(3) }) },
    { section, id: 'touchLeftHanded', label: 'Left-handed (stick on the right)', hint: 'Mirrors the layout: stick under the right thumb, buttons under the left.', kind: 'choice', get: () => onOff(s().touchLeftHanded), cycle: () => host.setSettings({ touchLeftHanded: !s().touchLeftHanded }) },
    { section, id: 'touchHaptics', label: 'Vibration', hint: 'A tick on every press and a thump on a hard knock (Android; not on iPhone).', kind: 'choice', get: () => onOff(s().touchHaptics), cycle: () => host.setSettings({ touchHaptics: !s().touchHaptics }) },
  ];
}

// ----------------------------------------------------------------------------- controls

export class ControlsScreen implements Screen {
  readonly root: HTMLElement;

  constructor(parent: HTMLElement, host: ScreenHost, private readonly onBack: () => void) {
    this.root = el('div', 'bh-screen bh-controls', undefined, parent);
    const panel = el('div', 'panel bh-paper', undefined, this.root);
    el('h2', '', 'Controls', panel);
    const b = host.bindings();
    const keys = (codes: string[]): string => [...new Set(codes.map(keyLabel))].join(' / ');
    const axis = (pos: string[], neg: string[]): string => `${keys(pos)} / ${keys(neg)}`;
    const touchUi = host.touch?.() ?? false;
    const rows: [string, string][] = touchUi
      ? [
          ['Walk · steer', 'Left thumb: drag anywhere low on the left'],
          ['Run', 'RUN (hold)'],
          ['Get in / out · interact', 'USE / OUT'],
          ['Accelerate · brake', 'GAS · BRAKE (or push the stick)'],
          ['Handbrake (slide!)', 'H-BRAKE'],
          ['Horn · radio', 'HORN · RADIO'],
          ['Punch · aim · next weapon', 'HIT · AIM (hold) · SWAP'],
          ['Map · pause · book', 'MAP · PAUSE · BOOK (top of the screen), or tap the minimap'],
          ['Online panels', 'BOOK → STREET (or hold BOOK)'],
          ['Left-handed, size, opacity', 'Settings'],
        ]
      : [
          ['Walk · steer · accelerate', 'W A S D  or  ↑ ← ↓ →'],
          ['Run', keys(b.keys.run)],
          ['Talk · use a place', keys(b.keys.enterExit)],
          ['Get in / out of a car', keys(b.keys.vehicle)],
          ['Handbrake (slide!)', keys(b.keys.handbrake)],
          ['Horn', keys(b.keys.horn)],
          ['Punch · swing · fire', keys(b.keys.attack)],
          ['Aim (hold) · lock on', keys(b.keys.aim)],
          ['Next weapon', keys(b.keys.weaponNext)],
          ['Radio station', keys(b.keys.radio)],
          ['Map', keys(b.keys.map)],
          ['Pause', keys(b.keys.pause)],
          ['Debug overlay', keys(b.keys.debug)],
          ['Pad: drive', 'RT gas · LT brake · L-stick'],
          ['Pad: act', 'Y enter · A handbrake · X / RB attack'],
          ['Pad: weapons', 'LT / LB aim · D-pad right next weapon'],
          ['Pad: menu', 'Start pause · D-pad radio'],
          ['Pad: map', 'Back / Select'],
        ];
    void axis;
    const cols = el('div', 'cols', undefined, panel);
    for (const [a, k] of rows) html('div', '', `<span>${esc(a)}</span><span>${esc(k)}</span>`, cols);
    html('div', 'bh-foot', touchUi ? '<span>Tap to close</span>' : '<span>Esc / Enter back</span>', panel);
    if (touchUi) this.root.addEventListener('click', () => this.onBack());
  }

  onNav(a: NavAction): boolean {
    if (a === 'back' || a === 'confirm') {
      this.onBack();
      return true;
    }
    return false;
  }

  dispose(): void {
    this.root.remove();
  }
}

// ----------------------------------------------------------------------------- mission intro card

export class IntroCard {
  readonly root: HTMLElement;
  private timer = 0;
  private done: (() => void) | null = null;

  constructor(parent: HTMLElement, m: MissionIntro, host: ScreenHost, seconds: number, onDone: () => void) {
    this.root = el('div', 'bh-screen bh-intro', undefined, parent);
    const card = el('div', 'card bh-glass blur', undefined, this.root);
    const tl = el('div', 'tagline', undefined, card);
    el('span', 'bh-tag', m.tag ?? 'NEW JOB', tl);
    el('span', 'bh-tag', 'CONFIDENTIAL', tl);
    // The client's photo clipped to the dossier (docs/UI.md, Painted art); nothing when they have none.
    if (pastePhoto(card, m.client, 'note')) card.classList.add('has-photo');
    el('h2', '', m.title, card);
    el('div', 'client', `Client: ${m.client}`, card);
    el('p', '', m.blurb, card);
    html('div', 'pay bh-stamp', `£${formatMoney(m.reward)}`, card);
    host.sound('whoosh');
    this.done = onDone;
    this.timer = window.setTimeout(() => this.leave(), seconds * 1000);
  }

  leave(): void {
    if (!this.done) return;
    const d = this.done;
    this.done = null;
    window.clearTimeout(this.timer);
    this.root.classList.add('leaving');
    window.setTimeout(() => {
      this.root.remove();
      d();
    }, 460);
  }

  dispose(): void {
    window.clearTimeout(this.timer);
    this.done = null;
    this.root.remove();
  }
}

// ----------------------------------------------------------------------------- result

const RESULT_STAMP: Record<ResultInfo['kind'], string> = {
  success: 'JOB DONE',
  failed: 'JOB BLOWN',
  busted: 'NICKED',
  wasted: 'WASTED',
};

export class ResultScreen implements Screen {
  readonly root: HTMLElement;
  private ready = false;
  private resolved = false;
  private readonly timers: number[] = [];
  private readonly press: HTMLElement;
  private readonly after: HTMLElement;
  private choices: MenuList | null = null;
  private readonly choiceBox: HTMLElement;

  constructor(parent: HTMLElement, private readonly info: ResultInfo, private readonly host: ScreenHost, private readonly onDone: (choice?: string) => void) {
    const cls = info.kind === 'success' ? 'success' : info.kind;
    this.root = el('div', `bh-screen bh-result ${cls}`, undefined, parent);
    // A dark glass panel (docs/UI.md, "Overlays"): the outcome scrolls inside `.r-body` when it is long, the
    // continue button / the choices are pinned in `.r-foot` so they are always on screen (a short phone
    // pushed them off the bottom of the old paper sheet).
    const panel = el('div', 'sheet bh-glass blur', undefined, this.root);
    const sheet = el('div', 'r-body', undefined, panel);
    const foot = el('div', 'r-foot', undefined, panel);
    const wrap = el('div', 'stamp-wrap', undefined, sheet);
    const stamp = el('div', 'bh-stamp', info.stamp ?? RESULT_STAMP[info.kind], wrap);
    stamp.style.visibility = 'hidden';
    // The job's stamp (copper / steel / brass) and its record line, inked a beat after the big one.
    let medal: HTMLElement | null = null;
    if (info.medal) {
      const row = el('div', `medal t${info.medal.tier}`, undefined, sheet);
      el('span', 'bh-stamp mstamp', info.medal.stamp, row);
      el('span', 'mnote', info.medal.note, row);
      row.style.visibility = 'hidden';
      medal = row;
    }
    if (info.title) el('div', 'rtitle', info.title, sheet);
    const sub = el('div', 'rsub', info.subtitle ?? '', sheet);
    sub.hidden = !info.subtitle;

    const lines = info.lines ?? [];
    const tally = el('ul', 'tally', undefined, sheet);
    tally.hidden = lines.length === 0;
    const total = el('div', 'total', undefined, sheet);
    total.hidden = lines.length === 0;
    el('span', '', 'Total', total);
    const totalVal = el('span', '', '£0', total);
    // What came of it (the next job), on the sheet itself rather than as a toast over it. The UI system
    // fills it in a frame later (`setAfter`): when the sheet is enqueued the board hasn't moved on yet.
    this.after = el('div', 'after', info.after ?? '', sheet);
    this.after.hidden = !info.after;
    // Continue: a real red stamp button on touch (a grey "tap to continue" was the sheet's only affordance).
    const touch = host.touch?.() ?? false;
    if (touch) {
      const b = el('button', 'press btn', 'Continue', foot);
      b.type = 'button';
      this.press = b;
    } else this.press = el('div', 'press', 'Press Enter to continue', foot);
    this.press.style.visibility = 'hidden';
    this.choiceBox = el('div', 'choices', undefined, foot);
    this.choiceBox.hidden = true;

    // Choreography: stamp slams in, tally ticks down, total rolls, then Enter is accepted.
    this.at(350, () => {
      stamp.style.visibility = 'visible';
      host.sound('stamp');
    });
    if (medal) {
      const m = medal;
      this.at(800, () => {
        m.style.visibility = 'visible';
        host.sound('stamp');
      });
    }
    let t = medal ? 1400 : 1100;
    let running = 0;
    lines.forEach((l) => {
      this.at(t, () => {
        const li = el('li', '', undefined, tally);
        el('span', '', l.label, li);
        el('b', l.amount < 0 ? 'neg' : '', `${l.amount < 0 ? '−' : ''}£${formatMoney(Math.abs(l.amount))}`, li);
        running += l.amount;
        totalVal.textContent = `${running < 0 ? '−' : ''}£${formatMoney(Math.abs(running))}`;
        host.sound(l.amount < 0 ? 'denied' : 'cash');
      });
      t += 520;
    });
    this.at(Math.max(t, 1500), () => this.becomeReady());
    this.root.addEventListener('click', (e) => {
      if (this.choices && (e.target as HTMLElement).closest('.choices')) return;
      this.onNav('confirm');
    });
  }

  private at(ms: number, fn: () => void): void {
    this.timers.push(window.setTimeout(fn, ms));
  }

  /** The "what came of it" line under the tally (null: none). */
  setAfter(text: string | null): void {
    setText(this.after, text ?? '');
    this.after.hidden = !text;
  }

  private becomeReady(): void {
    if (this.ready) return;
    this.ready = true;
    const actions = this.info.actions ?? [];
    if (actions.length === 0) {
      this.press.style.visibility = 'visible';
      return;
    }
    this.press.remove();
    this.choiceBox.hidden = false;
    this.choices = new MenuList(
      this.choiceBox,
      actions.map((a) => ({ id: a.id, label: a.label })),
      this.host,
      (id) => this.finish(id),
    );
  }

  private finish(choice?: string): void {
    if (this.resolved) return;
    this.resolved = true;
    this.onDone(choice);
  }

  onNav(a: NavAction): boolean {
    if (this.ready && this.choices) {
      // The choices sit in a row on the glass panel: left / right move along it like up / down.
      if (a === 'left') a = 'up';
      else if (a === 'right') a = 'down';
      if (a === 'back') {
        const acts = this.info.actions ?? [];
        this.host.sound('back');
        this.finish(acts[acts.length - 1]?.id);
        return true;
      }
      this.choices.nav(a);
      return true;
    }
    if (a !== 'confirm' && a !== 'back') return true; // swallow everything else while the screen is up
    if (!this.ready) {
      // Skip ahead: fast-forward the choreography instead of ignoring the key.
      if (this.timers.length) {
        for (const id of this.timers) window.clearTimeout(id);
        this.timers.length = 0;
        this.skipToEnd();
      }
      return true;
    }
    if (this.resolved) return true;
    this.host.sound('confirm');
    this.finish();
    return true;
  }

  private skipToEnd(): void {
    const sheet = this.root.querySelector('.r-body')!;
    const stamp = sheet.querySelector<HTMLElement>('.bh-stamp')!;
    stamp.style.visibility = 'visible';
    const medal = sheet.querySelector<HTMLElement>('.medal');
    if (medal) medal.style.visibility = 'visible';
    const tally = sheet.querySelector<HTMLElement>('.tally')!;
    const totalVal = sheet.querySelector<HTMLElement>('.total span:last-child')!;
    tally.textContent = '';
    let running = 0;
    for (const l of this.info.lines ?? []) {
      const li = el('li', '', undefined, tally);
      el('span', '', l.label, li);
      el('b', l.amount < 0 ? 'neg' : '', `${l.amount < 0 ? '−' : ''}£${formatMoney(Math.abs(l.amount))}`, li);
      running += l.amount;
    }
    totalVal.textContent = `${running < 0 ? '−' : ''}£${formatMoney(Math.abs(running))}`;
    this.becomeReady();
  }

  dispose(): void {
    for (const id of this.timers) window.clearTimeout(id);
    this.root.remove();
  }
}
